import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import config from '../../../config';
import Swal from 'sweetalert2';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './retourForm.scss';
import { resolveImageUrl, resolveImageVariant } from '../../../components/ndoe/imageVariant';
import { useFormGuard } from '../../../utils/formGuard';

// Vignette 30 px d'une option de Select, avec repli dérivé → original : sans lui,
// un dérivé manquant (rattrapage en cours) afficherait une image cassée.
const VignetteOption = ({ img }) => {
  const [replie, setReplie] = useState(false);
  return (
    <img
      src={replie ? resolveImageUrl(img) : resolveImageVariant(img, 30, 'auto')}
      alt=""
      loading="lazy"
      onError={() => !replie && setReplie(true)}
      style={{ width: 30, height: 30, objectFit: 'cover', borderRadius: 6 }}
    />
  );
};

// Un retour porte sur une LIGNE VENDUE précise (id_detail_commande), pas sur un
// produit vague : on choisit le client, puis une de ses lignes réellement achetées,
// et la quantité est plafonnée au reste retournable (vendu − déjà retourné).
// Le backend (applyCustomerReturn) re-vérifie tout en transaction.
const RetourForm = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [idClient, setIdClient] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [ligne, setLigne] = useState(null);
  const [quantite, setQuantite] = useState(1);
  const [motif, setMotif] = useState('');
  const [avarie, setAvarie] = useState(false);   // paire défectueuse → part en avariées, pas en vendable
  const [loadingLignes, setLoadingLignes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Garde « saisie non enregistrée » (chevron retour + Back Android) : dès qu'une
  // ligne est choisie ou un motif saisi, on confirme avant de quitter.
  useFormGuard(Boolean(ligne || motif));

  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${DOMAIN}/api/peuple`); setClients(data || []); } catch (e) { console.log(e); }
    })();
  }, [DOMAIN]);

  // Pré-remplissage depuis l'URL (bouton « Enregistrer un retour » ouvert depuis
  // une livraison terminée) : client d'abord, puis la ligne quand elle est chargée.
  useEffect(() => {
    const c = Number(searchParams.get('id_client'));
    if (Number.isInteger(c) && c > 0) setIdClient(c);
  }, [searchParams]);
  useEffect(() => {
    const d = Number(searchParams.get('id_detail_commande'));
    if (d && lignes.length) {
      const l = lignes.find((x) => Number(x.id_detail_commande) === d);
      if (l) { setLigne(l); setQuantite(1); }
    }
  }, [lignes, searchParams]);

  // Lignes retournables du client choisi.
  useEffect(() => {
    if (!idClient) { setLignes([]); setLigne(null); return undefined; }
    let alive = true;
    (async () => {
      setLoadingLignes(true);
      try {
        const { data } = await axios.get(`${DOMAIN}/api/vente/retournables?id_client=${idClient}`);
        if (alive) { setLignes(data || []); setLigne(null); }
      } catch (e) { console.log(e); } finally { if (alive) setLoadingLignes(false); }
    })();
    return () => { alive = false; };
  }, [idClient, DOMAIN]);

  const clientOptions = clients.map((c) => ({ value: c.id, label: c.nom }));
  const ligneOptions = lignes.map((l) => ({
    value: l.id_detail_commande,
    img: l.img,
    label: `${l.code_article || '—'} · ${l.nom_produit || ''} ${l.couleur || ''} P${l.taille ?? '?'} — reste ${l.qte_retournable}/${l.qte_vendue}`,
  }));

  const onSelectLigne = (opt) => {
    const l = opt ? lignes.find((x) => x.id_detail_commande === opt.value) || null : null;
    setLigne(l);
    setQuantite(1);
  };

  const max = ligne ? ligne.qte_retournable : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!idClient) return Swal.fire({ title: 'Client requis', text: 'Choisissez d’abord le client.', icon: 'warning' });
    if (!ligne) return Swal.fire({ title: 'Article requis', text: 'Choisissez la ligne vendue à retourner.', icon: 'warning' });
    const q = Number(quantite);
    if (!Number.isInteger(q) || q < 1 || q > max) return Swal.fire({ title: 'Quantité invalide', text: `Doit être entre 1 et ${max}.`, icon: 'warning' });
    setSubmitting(true);
    try {
      await axios.post(`${DOMAIN}/api/vente/retour`, {
        id_detail_commande: ligne.id_detail_commande,
        quantite: q,
        id_client: idClient,
        motif: motif || undefined,
        avarie,
      });
      await Swal.fire({
        title: 'Retour enregistré',
        text: avarie
          ? `${q} paire(s) mise(s) en avarie — hors stock vendable.`
          : `${q} paire(s) remise(s) en stock.`,
        icon: 'success',
        confirmButtonText: 'OK',
      });
      navigate('/retour');
    } catch (err) {
      Swal.fire({ title: 'Erreur', text: err?.response?.data?.error || err.message, icon: 'error', confirmButtonText: 'OK' });
    } finally { setSubmitting(false); }
    return undefined;
  };

  return (
    <div className="retourForm">
      <div className="product-container">
        <div className="product-container-top">
          <div className="product-left">
            <h2 className="product-h2">Ajouter un retour</h2>
            <span>Le retour ne peut porter que sur ce que le client a acheté — plafonné au reste retournable.</span>
          </div>
        </div>
        <div className="product-wrapper">
          <div className="product-container-bottom">
            <div className="form-controle">
              <label>Client</label>
              <Select options={clientOptions} placeholder="Choisir un client…" isClearable
                value={clientOptions.find((o) => o.value === idClient) || null}
                onChange={(o) => setIdClient(o ? o.value : null)} />
            </div>
            <div className="form-controle">
              <label>Article vendu à retourner</label>
              <Select
                options={ligneOptions}
                formatOptionLabel={(o) => (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {o.img
                      /* 30 px dans une option de Select — et toutes les options
                         se chargent d'un coup : c'est la plus petite image de
                         l'app, elle tirait pourtant l'original ~1254 px.
                         VignetteOption porte le repli dérivé → original. */
                      ? <VignetteOption img={o.img} />
                      : <span style={{ width: 30, height: 30, borderRadius: 6, background: '#f2ede3', display: 'inline-block' }} />}
                    <span>{o.label}</span>
                  </span>
                )}
                value={ligne ? ligneOptions.find((o) => o.value === ligne.id_detail_commande) : null}
                onChange={onSelectLigne}
                isDisabled={!idClient || loadingLignes}
                placeholder={!idClient ? 'Sélectionnez d’abord un client' : (loadingLignes ? 'Chargement…' : 'Choisir la ligne vendue…')}
                noOptionsMessage={() => (idClient ? 'Aucune vente retournable pour ce client' : 'Choisissez un client')}
              />
            </div>
            <div className="form-controle">
              <label>Quantité{ligne ? ` (max ${max})` : ''}</label>
              <input type="number" min={1} max={max || 1} className="form-input" value={quantite}
                onChange={(e) => setQuantite(e.target.value)} disabled={!ligne} />
            </div>
            <div className="form-controle">
              <label>Motif (optionnel)</label>
              <input type="text" className="form-input" placeholder="ex: pointure ne convient pas" value={motif}
                onChange={(e) => setMotif(e.target.value)} />
            </div>
            <div className="form-controle">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', lineHeight: 1.4 }}>
                <input type="checkbox" checked={avarie} onChange={(e) => setAvarie(e.target.checked)} style={{ marginTop: 2 }} />
                Paire défectueuse — mettre en avarie (elle ne revient pas au stock vendable)
              </label>
            </div>
          </div>

          {ligne && (
            <div className="retour-recap">
              Vendu&nbsp;: <b>{ligne.qte_vendue}</b> &middot; déjà retourné&nbsp;: <b>{ligne.qte_retournee}</b> &middot; retournable&nbsp;: <b>{ligne.qte_retournable}</b>
            </div>
          )}

          <div className="form-submit">
            <button className="btn-submit" onClick={submit} disabled={submitting}>{submitting ? 'Enregistrement…' : 'Soumettre'}</button>
            <button className="btn-submit btn-annuler" type="button" onClick={() => navigate('/retour')}>Annuler</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetourForm;
